import React from "react";
import { useTranslation } from "react-i18next";
import { Breadcrumbs, Link, Typography } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useNavigate } from "react-router-dom";
import theme, { ExtendedTypographyOptions } from "@/utils/theme";
import { useClientFilters } from "@/providers/ClientsFiltersContext";

interface ProfileBreadcrumbProps {
  participantName?: string;
}

const ProfileBreadcrumb: React.FC<ProfileBreadcrumbProps> = ({
  participantName,
}) => {
  const { t } = useTranslation("clients");
  const navigate = useNavigate();
  const { filters } = useClientFilters();

  // AC01: Breadcrumbs - "Participants list > Participant Profile"
  // Preserve filters when navigating back to participants list (COYN-915)
  const handleNavigateToParticipantsList = () => {
    const queryParams = new URLSearchParams();

    if (filters.search) queryParams.set("search", filters.search);
    if (filters.status.length > 0)
      queryParams.set("status", filters.status.join(","));
    if (filters.program) queryParams.set("program", filters.program);
    if (filters.orgUnitId) queryParams.set("orgUnitId", filters.orgUnitId);
    if (filters.groupLabelId) queryParams.set("groupLabelId", filters.groupLabelId);
    if (filters.notEnrolled)
      queryParams.set("notEnrolled", String(filters.notEnrolled));
    if (filters.sortField) queryParams.set("sortField", filters.sortField);
    if (filters.sortOrder)
      queryParams.set("sortOrder", filters.sortOrder.toString());
    if (filters.pageNumber)
      queryParams.set("pageNumber", filters.pageNumber.toString());
    if (filters.pageSize)
      queryParams.set("pageSize", filters.pageSize.toString());

    const queryString = queryParams.toString();
    navigate(`/participants${queryString ? `?${queryString}` : ""}`);
  };

  return (
    <Breadcrumbs
      separator={<ChevronRightIcon />}
      aria-label="breadcrumb"
      sx={{ flexWrap: "wrap", minWidth: 0 }}
    >
      <Link
        component="button"
        onClick={handleNavigateToParticipantsList}
        textTransform={"uppercase"}
        underline="hover"
        color={theme.palette.primary.main}
        fontSize={
          (theme.typography as ExtendedTypographyOptions).labelMedium?.fontSize
        }
        fontWeight="bold"
        sx={{
          cursor: "pointer",
          border: "none",
          background: "none",
          padding: 0,
        }}
      >
        {t("client_profile.breadcrumb_clients") || "Participants list"}
      </Link>
      <Typography
        fontSize={
          (theme.typography as ExtendedTypographyOptions).labelMedium?.fontSize
        }
        fontWeight="bold"
        textTransform={"uppercase"}
        color="#7D7979"
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: "100%",
        }}
      >
        {participantName ||
          t("client_profile.breadcrumb_client_profile") ||
          "Participant Profile"}
      </Typography>
    </Breadcrumbs>
  );
};

export default ProfileBreadcrumb;
